<template>
    <!--
        Модальне вікно вибору орієнтовної дати виконання тікета.

        Використовується у двох режимах (prop mode):
        - 'inJob' (за замовчуванням) — з'являється в момент взяття тікета в роботу.
          Дата НЕ обов'язкова: якщо її не обрано, кнопка перетворюється на
          "Взяти в роботу без дати" і тікет береться в роботу без орієнтовної дати.
          Закриття хрестиком / Esc = ПОВНА відмова, тікет в роботу не береться.
        - 'edit' — редагування вже існуючої орієнтовної дати з картки тікета (олівець біля дати).
    -->
    <dialogBox @close="this.$emit('close')">
        <template v-slot:title>
            {{ this.mode === 'edit' ? $t('editEstimatedDate') : $t('estimatedDate') }}
        </template>

        <template v-slot:body>
            <!-- Підказка, що поле необов'язкове -->
            <p class="text-muted mb-3">{{ $t('estimatedDateHint') }}</p>

            <!-- Швидкі кнопки-пресети -->
            <div class="d-flex flex-wrap gap-2 mb-3">
                <button
                    v-for="preset in presets"
                    :key="preset.key"
                    type="button"
                    class="btn btn-sm"
                    :class="estimatedDate === preset.value ? 'btn-info' : 'btn-soft-info'"
                    @click="estimatedDate = preset.value"
                >
                    {{ preset.label }}
                </button>
            </div>

            <!-- Календар -->
            <div class="mb-2">
                <label class="form-label">{{ $t('date') }}</label>
                <flat-pickr
                    v-model="estimatedDate"
                    :config="config"
                    class="form-control"
                    :placeholder="$t('estimatedDate')"
                ></flat-pickr>
            </div>

            <!-- Скинути обраний вибір -->
            <a
                v-if="estimatedDate"
                class="link-danger copy text-decoration-underline fs-13"
                @click="estimatedDate = ''"
            >
                {{ $t('Clear') }}
            </a>
        </template>

        <template v-slot:footer-bottom>
            <!--
                Режим взяття в роботу: одна кнопка, яка сама пояснює результат.
                Без обраної дати -> "Взяти в роботу без дати", з датою -> "Взяти в роботу".
                Двох окремих кнопок ("Пропустити" / "Взяти в роботу") немає свідомо:
                без дати вони робили абсолютно однакову дію і збивали з пантелику.
            -->
            <template v-if="this.mode !== 'edit'">
                <button
                    type="button"
                    class="btn btn-success"
                    @click="submit()"
                >
                    {{ estimatedDate ? $t('takeToWork') : $t('takeToWorkWithoutDate') }}
                </button>
            </template>

            <!-- Режим редагування -->
            <template v-else>
                <button
                    type="button"
                    class="btn link-danger fw-medium"
                    @click="this.$emit('close')"
                >
                    {{ $t('cancel') }}
                </button>
                <button
                    type="button"
                    class="btn btn-success"
                    @click="submit()"
                >
                    {{ $t('save') }}
                </button>
            </template>
        </template>
    </dialogBox>
</template>

<script>
import dialogBox from '@/components/modal/dialog'
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { Russian } from "flatpickr/dist/l10n/ru.js";
import { Ukrainian } from "flatpickr/dist/l10n/uk.js";
import { storeS } from '@/store'

export default {
    props: {
        // об'єкт тікета (потрібен, щоб підтягнути вже збережену дату в режимі редагування)
        obj: {
            type: Object,
            default: () => ({})
        },
        // 'inJob' - взяття в роботу, 'edit' - редагування дати
        mode: {
            type: String,
            default: 'inJob'
        }
    },
    components: {
        dialogBox,
        flatPickr
    },
    emits: ['close', 'confirm'],
    data(){
        return {
            estimatedDate: '', // обрана орієнтовна дата у форматі Y-m-d
            config: {
                mode: "single",
                minDate: "today", // орієнтовну дату в минулому ставити немає сенсу
                altInput: true,
                altFormat: "F j, Y",
                enableTime: false,
                dateFormat: "Y-m-d",
                locale: ""
            }
        }
    },
    created(){
        // локаль календаря відповідно до мови інтерфейсу
        if(storeS.lang == 'uk'){
            this.config.locale = Ukrainian
        } else if(storeS.lang == 'ru'){
            this.config.locale = Russian
        }

        // в режимі редагування підставляємо вже збережену дату
        if(this.obj && this.obj.estimatedDate) {
            this.estimatedDate = String(this.obj.estimatedDate).split(' ')[0]
        }
    },
    methods: {
        /**
         * Підтвердження вибору.
         * Якщо дата не обрана — віддаємо null (тікет береться в роботу без орієнтовної дати).
         */
        submit(){
            this.$emit('confirm', this.estimatedDate || null)
        },

        /**
         * Дата у форматі Y-m-d зі зсувом у днях від сьогодні
         * @param {Number} days - кількість днів від сьогодні
         */
        dateFromToday(days){
            const date = new Date();
            date.setDate(date.getDate() + days);

            return date.getFullYear() + "-" +
                ("00" + (date.getMonth() + 1)).slice(-2) + "-" +
                ("00" + date.getDate()).slice(-2);
        }
    },
    computed: {
        // швидкі пресети вибору дати
        presets(){
            return [
                { key: 'today', label: this.$t('Today'), value: this.dateFromToday(0) },
                { key: 'tomorrow', label: this.$t('Tomorrow'), value: this.dateFromToday(1) },
                { key: 'threeDays', label: this.$t('inThreeDays'), value: this.dateFromToday(3) },
                { key: 'week', label: this.$t('inWeek'), value: this.dateFromToday(7) },
            ]
        }
    }
}
</script>
